package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.cityLabel
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_error
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_retry
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_add_photo_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_add_photos
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_business_location
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_disclaimer
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_experience_q
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_star_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_stood_out
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_submit
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_tag_clean_location
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_tag_fast_response
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_tag_friendly_service
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_tag_good_price
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_tag_professional
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_tag_recommended
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_tap_to_rate
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_thoughts_label
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_thoughts_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_title
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_submit_failed
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_upload
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** The draft a reviewer produces on this screen; persisted in Phase 2. */
data class ReviewDraft(
    val rating: Int,
    val highlightTagKeys: List<String>,
    val comment: String,
)

/**
 * Stateful entry: loads the business header, renders the form, and drives the submit flow.
 * [onSubmitted] fires after the review is persisted (host pops back — the detail screen's live
 * reviews + server-recomputed rating reflect it); [onRequireSignIn] routes guests to sign-in.
 */
@Composable
fun WriteReviewRoute(
    businessId: String,
    onBack: () -> Unit,
    onSubmitted: () -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: WriteReviewViewModel = koinViewModel(parameters = { parametersOf(businessId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    LaunchedEffect(state.submitStatus) {
        when (state.submitStatus) {
            ReviewSubmitStatus.Submitted -> onSubmitted()
            ReviewSubmitStatus.SignInRequired -> {
                viewModel.consumeSubmitStatus()
                onRequireSignIn()
            }
            else -> Unit // Failed renders inline; null is idle.
        }
    }
    WriteReviewScreen(
        state = state,
        onBack = onBack,
        onSubmit = viewModel::submit,
        onRetry = viewModel::retry,
        modifier = modifier,
    )
}

/** One selectable "what stood out" highlight: a stable [key] (stored on the draft) + its localized label. */
private data class HighlightTag(val key: String, val label: StringResource)

private val HIGHLIGHT_TAGS = listOf(
    HighlightTag("friendly_service", Res.string.review_tag_friendly_service),
    HighlightTag("good_price", Res.string.review_tag_good_price),
    HighlightTag("professional", Res.string.review_tag_professional),
    HighlightTag("fast_response", Res.string.review_tag_fast_response),
    HighlightTag("clean_location", Res.string.review_tag_clean_location),
    HighlightTag("recommended", Res.string.review_tag_recommended),
)

/**
 * Write a Review — Stitch `96288a10` (Directory & Community System). Tokens + string resources only.
 * Stateless; form state (rating/highlights/comment) is local. Submitting hands a [ReviewDraft] to the
 * host — **persistence is Phase 2** (auth + `reviews/{id}` write + aggregate recompute).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun WriteReviewScreen(
    state: WriteReviewUiState,
    onBack: () -> Unit,
    onSubmit: (ReviewDraft) -> Unit,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        // Teal top bar
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.primaryContainer)
                .statusBarsPadding()
                .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.review_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onPrimary,
            )
            Text(
                text = stringResource(Res.string.review_title),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onPrimary,
            )
        }

        when {
            state.loading -> LoadingState()
            state.error || state.business == null -> ErrorState(
                message = stringResource(Res.string.business_error),
                onRetry = onRetry,
                retryLabel = stringResource(Res.string.business_retry),
            )
            else -> ReviewForm(
                business = state.business,
                submitting = state.submitting,
                failed = state.submitStatus == ReviewSubmitStatus.Failed,
                onSubmit = onSubmit,
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ReviewForm(
    business: Business,
    submitting: Boolean,
    failed: Boolean,
    onSubmit: (ReviewDraft) -> Unit,
) {
    val lang = AppTheme.language
    var rating by remember { mutableStateOf(0) }
    var selectedKeys by remember { mutableStateOf(emptySet<String>()) }
    var comment by remember { mutableStateOf("") }

    Column(
        modifier = Modifier
            .fillMaxHeight()
            .appContentWidth(AppTheme.sizes.contentMaxWidthCompact)
            .verticalScroll(rememberScrollState())
            .navigationBarsPadding()
            .padding(AppTheme.spacing.lg),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
    ) {
        // Business identity
        Card {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                Box(
                    modifier = Modifier
                        .size(AppTheme.sizes.avatarLg)
                        .background(AppTheme.colors.primaryContainer, AppTheme.shapes.tile),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = business.displayName(lang).take(2).uppercase(),
                        style = AppTheme.typography.headlineMd,
                        color = AppTheme.colors.onPrimary,
                    )
                }
                Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
                    Text(
                        text = business.displayName(lang),
                        style = AppTheme.typography.headlineMd,
                        color = AppTheme.colors.onSurface,
                    )
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                    ) {
                        AppIcon(name = "location_on", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurfaceVariant)
                        Text(
                            text = stringResource(Res.string.review_business_location, AppTheme.language.cityLabel(business.address.city), business.address.province),
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.onSurfaceVariant,
                        )
                    }
                }
            }
        }

        // Rating
        Card {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                Text(
                    text = stringResource(Res.string.review_experience_q),
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                )
                Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                    for (star in 1..5) {
                        AppIcon(
                            name = "star",
                            size = AppTheme.sizes.iconLg,
                            tint = if (star <= rating) AppTheme.colors.secondary else AppTheme.colors.outline,
                            filled = star <= rating,
                            contentDescription = stringResource(Res.string.review_star_a11y, star),
                            modifier = Modifier.clickable { rating = star },
                        )
                    }
                }
                Text(
                    text = stringResource(Res.string.review_tap_to_rate),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }
        }

        // What stood out
        SectionLabel(stringResource(Res.string.review_stood_out))
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            HIGHLIGHT_TAGS.forEach { tag ->
                val selected = tag.key in selectedKeys
                AppChip(
                    label = stringResource(tag.label),
                    selected = selected,
                    onClick = {
                        selectedKeys = if (selected) selectedKeys - tag.key else selectedKeys + tag.key
                    },
                )
            }
        }

        // Thoughts
        SectionLabel(stringResource(Res.string.review_thoughts_label))
        AppTextField(
            value = comment,
            onValueChange = { comment = it },
            placeholder = stringResource(Res.string.review_thoughts_placeholder),
            singleLine = false,
            modifier = Modifier.heightIn(min = AppTheme.sizes.reviewCommentMinHeight),
        )

        // Photos (upload wired in Phase 2 — Storage)
        SectionLabel(stringResource(Res.string.review_add_photos))
        Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
            UploadTile(modifier = Modifier.weight(1f))
            Box(modifier = Modifier.weight(1f).aspectRatio(1f).background(AppTheme.colors.surfaceContainer, AppTheme.shapes.card))
            Box(modifier = Modifier.weight(1f).aspectRatio(1f).background(AppTheme.colors.surfaceContainer, AppTheme.shapes.card))
        }

        if (failed) {
            Text(
                text = stringResource(Res.string.review_submit_failed),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onErrorContainer,
                textAlign = TextAlign.Center,
                modifier = Modifier
                    .fillMaxWidth()
                    .background(AppTheme.colors.errorContainer, AppTheme.shapes.card)
                    .padding(AppTheme.spacing.md),
            )
        }
        AppButton(
            text = stringResource(Res.string.review_submit),
            onClick = { onSubmit(ReviewDraft(rating, selectedKeys.toList(), comment.trim())) },
            modifier = Modifier.fillMaxWidth(),
            trailingIcon = "send",
            enabled = rating > 0 && !submitting,
            loading = submitting,
        )
        Text(
            text = stringResource(Res.string.review_disclaimer),
            style = AppTheme.typography.bodySm,
            color = AppTheme.colors.onSurfaceVariant,
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun Card(content: @Composable () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.card)
            .padding(AppTheme.spacing.lg),
    ) { content() }
}

@Composable
private fun SectionLabel(text: String) {
    Text(text = text, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurfaceVariant)
}

@Composable
private fun UploadTile(modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .aspectRatio(1f)
            .border(AppTheme.sizes.borderWidth, AppTheme.colors.outlineVariant, AppTheme.shapes.card),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        AppIcon(name = "add_a_photo", tint = AppTheme.colors.onSurfaceVariant, contentDescription = stringResource(Res.string.review_add_photo_a11y))
        Text(
            text = stringResource(Res.string.review_upload),
            style = AppTheme.typography.labelSm,
            color = AppTheme.colors.onSurfaceVariant,
            modifier = Modifier.padding(top = AppTheme.spacing.xxs),
        )
    }
}
